import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Moon } from 'lucide-react';

const LiquidFlow = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const [mode, setMode] = useState('day');
  const modeRef = useRef(mode);
  const PARTICLE_COUNT = 2000;

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);

    initParticles();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const initParticles = () => {
    particlesRef.current = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particlesRef.current.push({
        x: Math.random() * canvasRef.current.width,
        y: Math.random() * canvasRef.current.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1
      });
    }
  };

  const animate = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const currentMode = modeRef.current;

    ctx.fillStyle = currentMode === 'neon' ? 'rgba(0, 0, 20, 0.2)' : 'rgba(253, 251, 247, 0.2)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    particlesRef.current.forEach(p => {
      const dx = mouseRef.current.x - p.x;
      const dy = mouseRef.current.y - p.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > 0 && dist < 150) {
        const force = (150 - dist) / 150;
        p.vx += (dx / dist) * force * 0.5;
        p.vy += (dy / dist) * force * 0.5;
      }

      p.vx *= 0.95;
      p.vy *= 0.95;
      p.x += p.vx;
      p.y += p.vy;

      if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
      if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

      p.x = Math.max(0, Math.min(canvas.width, p.x));
      p.y = Math.max(0, Math.min(canvas.height, p.y));

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      
      if (currentMode === 'neon') {
        const hue = (p.x / canvas.width) * 360;
        ctx.fillStyle = `hsla(${hue}, 100%, 60%, 0.8)`;
        ctx.shadowBlur = 10;
        ctx.shadowColor = `hsl(${hue}, 100%, 60%)`;
      } else {
        ctx.fillStyle = 'rgba(129, 178, 154, 0.6)';
        ctx.shadowBlur = 0;
      }
      
      ctx.fill();
    });

    animationRef.current = requestAnimationFrame(animate);
  };

  const handlePointerMove = (e) => {
    mouseRef.current = { x: e.clientX, y: e.clientY };
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: mode === 'neon' ? '#000014' : '#FDFBF7' }}>
      <canvas
        ref={canvasRef}
        onPointerMove={handlePointerMove}
        className="absolute inset-0"
        style={{ touchAction: 'none' }}
      />

      <div className="relative z-10 p-6">
        <header className="flex justify-between items-center mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="glass-effect text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-white/80 transition-all duration-300 flex items-center gap-2 shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-[#2D3748]" style={{ color: mode === 'neon' ? '#FFFFFF' : '#2D3748' }}>
            Liquid Flow
          </h1>
          
          <button
            data-testid="toggle-mode"
            onClick={() => setMode(mode === 'day' ? 'neon' : 'day')}
            className="glass-effect px-6 py-3 rounded-full font-semibold hover:bg-white/80 transition-all duration-300 shadow-lg flex items-center gap-2"
            style={{ color: mode === 'neon' ? '#FFFFFF' : '#2D3748' }}
          >
            {mode === 'day' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            {mode === 'day' ? 'Neon' : 'Day'}
          </button>
        </header>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 glass-effect px-6 py-4 rounded-2xl">
          <p className="text-center" style={{ color: mode === 'neon' ? '#FFFFFF' : '#4A5568' }}>
            Move your pointer to create flowing currents
          </p>
        </div>
      </div>
    </div>
  );
};

export default LiquidFlow;